import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, InputNumber, Table, Modal, message, Tag, Space, Typography, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { PageWrapper, StandardCard } from '../../common';
import { theme } from '../../../theme';

const { Text, Paragraph } = Typography;

interface ProduccionAjuste {
  id: number;
  numero_produccion: number;
  cliente: string;
  producto: string;
  kg_fibra: number;
  kilos_semilla_actual: number;
  kilos_semilla_nuevo: number;
  rinde_semilla_actual: number;
  rinde_semilla_nuevo: number;
}

interface CicloOption {
  id: number;
  descripcion: string;
}

const AjustarSemilla: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();

  const [ciclos, setCiclos] = useState<CicloOption[]>([]);
  const [idCiclo, setIdCiclo] = useState<number | null>(null);
  const [desde, setDesde] = useState<number | null>(null);
  const [hasta, setHasta] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [coeficiente, setCoeficiente] = useState<number | null>(null);
  const [coeficienteNoConfigurado, setCoeficienteNoConfigurado] = useState(false);
  const [resultados, setResultados] = useState<ProduccionAjuste[]>([]);
  const [incluidas, setIncluidas] = useState<Set<number>>(new Set());
  const [busquedaRealizada, setBusquedaRealizada] = useState(false);

  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal || 1;

  // Cargar ciclos y seleccionar el último por defecto
  useEffect(() => {
    const cargarCiclos = async () => {
      if (!id_empresa) return;
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_PRODUCCION_URL}/listar_ciclos/${id_empresa}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await response.json();
        if (data.status === 'success') {
          const lista: CicloOption[] = data.data || [];
          setCiclos(lista);
          if (lista.length > 0) {
            setIdCiclo(lista[0].id); // viene ordenado por -id, el primero es el último ciclo
          }
        }
      } catch (error) {
        console.error('Error al cargar ciclos:', error);
      }
    };
    cargarCiclos();
  }, [getAccessTokenSilently, id_empresa]);

  const handlePrevisualizar = async () => {
    if (!desde || !hasta) {
      message.warning('Ingrese el rango de producción desde/hasta');
      return;
    }
    if (!id_empresa) {
      message.error('No se pudo obtener la información de la empresa');
      return;
    }
    if (!idCiclo) {
      message.warning('Seleccione un ciclo');
      return;
    }

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_PRODUCCION_URL}/ajustar_semilla_preview/${id_empresa}/${id_sucursal}/${desde}/${hasta}?id_ciclo=${idCiclo}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();

      if (data.status === 'success') {
        setResultados(data.data || []);
        setCoeficiente(data.coeficiente ?? 0);
        setCoeficienteNoConfigurado(!!data.coeficiente_no_configurado);
        setIncluidas(new Set((data.data || []).map((p: ProduccionAjuste) => p.id)));
        setBusquedaRealizada(true);
        if ((data.data || []).length === 0) {
          message.info('No se encontraron producciones de Fibra en el rango indicado para el ciclo seleccionado');
        }
      } else {
        message.error(data.message || 'Error al previsualizar el ajuste');
      }
    } catch (error) {
      console.error('Error al previsualizar:', error);
      message.error('Error de conexión al previsualizar el ajuste');
    } finally {
      setLoading(false);
    }
  };

  const toggleIncluida = (id: number) => {
    setIncluidas(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAplicar = () => {
    const seleccionadas = resultados.filter(r => incluidas.has(r.id));

    Modal.confirm({
      title: 'Confirmar ajuste de semilla',
      content: `Se van a actualizar ${seleccionadas.length} de ${resultados.length} producciones del rango ${desde}–${hasta} con el coeficiente actual (${coeficiente}). Esta acción no se puede deshacer.`,
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          setAplicando(true);
          const token = await getAccessTokenSilently();
          const response = await fetch(
            `${process.env.REACT_APP_PRODUCCION_URL}/ajustar_semilla_aplicar/${id_empresa}/${id_sucursal}`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id_ciclo: idCiclo,
                producciones: seleccionadas.map(r => ({
                  id: r.id,
                  kilos_semilla_nuevo: r.kilos_semilla_nuevo,
                })),
              }),
            }
          );
          const data = await response.json();

          if (data.status === 'success') {
            message.success(`Ajuste aplicado: ${data.actualizadas} producciones actualizadas correctamente.`);
            setResultados([]);
            setIncluidas(new Set());
            setBusquedaRealizada(false);
          } else {
            message.error(data.message || 'Error al aplicar el ajuste');
          }
        } catch (error) {
          console.error('Error al aplicar el ajuste:', error);
          message.error('Error de conexión al aplicar el ajuste');
        } finally {
          setAplicando(false);
        }
      },
    });
  };

  const handleToggleTodas = (checked: boolean) => {
    setIncluidas(checked ? new Set(resultados.map(r => r.id)) : new Set());
  };

  const columns: ColumnsType<ProduccionAjuste> = [
    {
      title: (
        <input
          type="checkbox"
          checked={resultados.length > 0 && incluidas.size === resultados.length}
          ref={(el) => {
            if (el) {
              el.indeterminate = incluidas.size > 0 && incluidas.size < resultados.length;
            }
          }}
          onChange={(e) => handleToggleTodas(e.target.checked)}
          title={incluidas.size === resultados.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
        />
      ),
      key: 'incluir',
      width: 60,
      align: 'center',
      render: (_, record) => (
        <input
          type="checkbox"
          checked={incluidas.has(record.id)}
          onChange={() => toggleIncluida(record.id)}
        />
      ),
    },
    { title: 'Nro. Producción', dataIndex: 'numero_produccion', key: 'numero_produccion', width: 120, align: 'center' },
    { title: 'Cliente', dataIndex: 'cliente', key: 'cliente', width: 180 },
    { title: 'Producto', dataIndex: 'producto', key: 'producto', width: 100 },
    {
      title: 'Kg. Fibra', dataIndex: 'kg_fibra', key: 'kg_fibra', width: 110, align: 'right',
      render: (v: number) => v.toFixed(2),
    },
    {
      title: 'Kg. Semilla Anterior (BD)', dataIndex: 'kilos_semilla_actual', key: 'kilos_semilla_actual', width: 150, align: 'right',
      render: (v: number) => v.toFixed(2),
    },
    {
      title: 'Kg. Semilla Nuevo', dataIndex: 'kilos_semilla_nuevo', key: 'kilos_semilla_nuevo', width: 130, align: 'right',
      render: (v: number) => <Text strong style={{ color: theme.colors.secondary }}>{v.toFixed(2)}</Text>,
    },
    {
      title: 'Rinde Semilla % Anterior (BD)', dataIndex: 'rinde_semilla_actual', key: 'rinde_semilla_actual', width: 170, align: 'right',
      render: (v: number) => `${v.toFixed(2)}%`,
    },
    {
      title: 'Rinde Semilla % Nuevo', dataIndex: 'rinde_semilla_nuevo', key: 'rinde_semilla_nuevo', width: 150, align: 'right',
      render: (v: number) => <Text strong style={{ color: theme.colors.secondary }}>{v.toFixed(2)}%</Text>,
    },
  ];

  const cantidadIncluidas = incluidas.size;

  return (
    <PageWrapper>
      <StandardCard title="Herramientas › Ajustar Semilla" style={{ marginBottom: '24px' }}>
        <Paragraph style={{ color: theme.colors.textSecondary, marginBottom: '16px' }}>
          Recalcula <Text strong>Kilos Semilla</Text> y <Text strong>Rinde Semilla %</Text> de las producciones de Fibra
          en un rango, usando el coeficiente vigente configurado en Configuración &gt; Producción &gt; Cálculo de Semillas.
        </Paragraph>

        {coeficiente !== null && (
          <Tag color={coeficienteNoConfigurado ? 'red' : 'blue'} style={{ padding: '6px 12px', fontSize: 13, marginBottom: 16 }}>
            Coeficiente vigente (SEM): {coeficiente}
          </Tag>
        )}

        <Space size="large" align="end" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 600, fontSize: 12.5, color: theme.colors.textPrimary }}>Ciclo</div>
            <Select
              value={idCiclo}
              onChange={setIdCiclo}
              style={{ width: 200 }}
              options={ciclos.map(c => ({ label: c.descripcion, value: c.id }))}
              placeholder="Seleccionar ciclo"
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 600, fontSize: 12.5, color: theme.colors.textPrimary }}>Producción desde</div>
            <InputNumber min={1} value={desde} onChange={setDesde} style={{ width: 160 }} />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 600, fontSize: 12.5, color: theme.colors.textPrimary }}>Producción hasta</div>
            <InputNumber min={1} value={hasta} onChange={setHasta} style={{ width: 160 }} />
          </div>
          <Button type="primary" icon={<SearchOutlined />} onClick={handlePrevisualizar} loading={loading}>
            Previsualizar
          </Button>
        </Space>
      </StandardCard>

      {busquedaRealizada && resultados.length > 0 && (
        <StandardCard title={`Resultado: ${resultados.length} producciones de FIBRA encontradas en el rango ${desde}–${hasta}`}>
          <Table
            columns={columns}
            dataSource={resultados}
            rowKey="id"
            pagination={false}
            scroll={{ x: 'max-content' }}
            size="small"
          />

          <div style={{
            marginTop: 16,
            padding: '12px 16px',
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: theme.styles.borderRadius,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}>
            <Text>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
              <Text strong>{cantidadIncluidas}</Text> de <Text strong>{resultados.length}</Text> producciones serán modificadas.
            </Text>
            <Button
              type="primary"
              onClick={handleAplicar}
              loading={aplicando}
              disabled={cantidadIncluidas === 0 || coeficienteNoConfigurado}
            >
              Aplicar Ajuste
            </Button>
          </div>

          {coeficienteNoConfigurado && (
            <Paragraph style={{ color: '#cf1322', marginTop: 12, marginBottom: 0 }}>
              El coeficiente de semilla no está configurado (o es 0). Configurelo en Configuración &gt; Producción &gt; Cálculo de Semillas antes de aplicar el ajuste.
            </Paragraph>
          )}
        </StandardCard>
      )}
    </PageWrapper>
  );
};

export default AjustarSemilla;
